import React, { useState } from 'react';

import { useMutation } from '@betterment/js-runtime';
import { isToday } from 'date-fns';

import CancellationModal from './CancellationModal';
import CategoryUpdateConfirmationModal from './CategoryUpdateConfirmationModal';
import { ActiveModal, HandleCategoryUpdate, TransactionUpdateBody } from './data';
import Detail from './Detail';

import { useFlash } from '../../../Shared/contexts/FlashContext';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { DetailedTransaction } from '../../data';
import { useAccount } from '../AccountProvider';
import { TransactionCategory } from '../TransactionIcon';
import { useTransactions } from '../TransactionsContext';

export interface Props extends DetailedTransaction {
  closeModal: () => void;
  accountType: string;
}

const TransactionModal: React.FC<React.PropsWithChildren<Props>> = ({ closeModal, accountType, ...transaction }) => {
  const [activeModal, setActiveModal] = useState<ActiveModal>('category-update-confirmation');
  const [accountRuleElgibileCategory, setAccountRuleElgibileCategory] = useState<TransactionCategory>();
  const { account } = useAccount();
  const { setFlashValues } = useFlash();
  const { replaceTransaction, markMatchingTransactionsAsDirty } = useTransactions(null, {
    skip: true,
  });

  const [updateTransaction] = useMutation<DetailedTransaction, TransactionUpdateBody>(
    urlBuilder.apiV2CheckingDetailedTransaction.absolute({ accountId: account.id, transactionId: transaction.id }),
    { method: 'PUT' }
  );

  const setError = () => {
    setFlashValues({
      variant: 'warning',
      message: 'Something went wrong and we were unable to update the category for this transaction',
    });
  };

  const handleCategoryUpdate: HandleCategoryUpdate = async ({ category: newCategory, shouldCreateRule }) => {
    try {
      const { data: updatedTransaction, errors } = await updateTransaction({ category: newCategory, shouldCreateRule });

      if (errors) {
        setError();
      } else if (shouldCreateRule) {
        markMatchingTransactionsAsDirty(({ id, merchant, createdAt, category }) => {
          return (
            id === transaction.id ||
            (Boolean(merchant) &&
              merchant?.name === transaction.merchant?.name &&
              newCategory !== category &&
              isToday(new Date(createdAt)))
          );
        });

        setFlashValues({
          variant: 'success',
          message: `Category will be applied to all future ${transaction.merchant?.name ?? ''} transactions.`,
        });
      } else if (updatedTransaction) {
        replaceTransaction(updatedTransaction);
        setFlashValues(null);
      }
    } catch {
      setError();
    }
  };

  if (activeModal === 'cancellation') {
    return (
      <CancellationModal
        accountType={accountType}
        transaction={transaction}
        onClose={() => setActiveModal('details')}
      />
    );
  }

  if (activeModal === 'category-update-confirmation' && accountRuleElgibileCategory) {
    return (
      <CategoryUpdateConfirmationModal
        transaction={transaction}
        onClose={() => setActiveModal('details')}
        accountRuleElgibileCategory={accountRuleElgibileCategory}
        handleCategoryUpdate={handleCategoryUpdate}
      />
    );
  }

  return (
    <Detail
      transaction={transaction}
      onClose={closeModal}
      handleCancel={() => setActiveModal('cancellation')}
      setActiveModal={setActiveModal}
      setAccountRuleElgibileCategory={setAccountRuleElgibileCategory}
      handleCategoryUpdate={handleCategoryUpdate}
    />
  );
};

export default TransactionModal;
